import './ProductionComponents.scss';
import {Box} from '@mui/material';
import {FieldErrors, FormProvider, useForm} from 'react-hook-form';
import {t} from 'i18next';
import {
  ProductionComponentsFormData,
  productionComponentsSchema,
} from '../../../../schemas/productions/productionSchema';
import {zodResolver} from '@hookform/resolvers/zod';
import {
  useState,
  forwardRef,
  useImperativeHandle,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from 'react';
import ArrAccordionWithCheckbox from '../../../common/arr-accordion-with-checkbox/ArrAccordionWithCheckbox';
import OptionalInstruction from './OptionalInstruction';
import TiffPdfComponent, {
  TiffPdfValues,
} from './tiff-pdf-component/TiffPdfComponent';
import DatComponent from './dat-component/DatComponent';
import {
  useGetProductionComponents,
  useSaveProductionComponents,
  useGetTranslationComponent,
  useGetTemplateComponents,
  useGetTemplateTranslationComponent,
} from '../../../../hooks/productions/useProductions';
import {useDispatch, useSelector} from 'react-redux';
import {RootState} from '../../../../redux/store';
import {TreeViewBaseItem} from '@mui/x-tree-view';
import {ExtendedTreeItemProps} from '../../../../shared/interfaces';
import {ISelectItem} from '../../../../shared/types';
import {
  TiffComponentModel,
  GetTemplateDetailsRequestParam,
} from '../../../../api/types/production.type';
import {NativeComponent} from './native-components/NativeComponent';
import {NativeRedactionSettingsState} from './native-components/nativeRedactionUtils';
import TextComponent from './text-component/TextComponent';
import {
  DEFAULT_FONT_FAMILY,
  DEFAULT_FONT_STYLE,
  DEFAULT_FONT_SIZE,
  PreserveColorOption,
  DocumentSelectionMethod,
  ModuleType,
  FormAction,
  ToastType,
  FormMode,
  ProductionStatus,
} from '../CreateNewProductionUtils';

// Component Constants
// DAT field category constants
const DAT_FIELD_CATEGORY_FIELD = 'Field';
const DAT_FIELD_CATEGORY_TAGS = 'Tags';

// DAT field type constants
const DAT_FIELD_TYPE_FIELDS = 'FIELDS';
const DAT_FIELD_TYPE_TAGS = 'TAGS';

// Default DAT field constants
const DEFAULT_DAT_SOURCE_FIELD_ID = '-146';
const DEFAULT_DAT_FIELD_HEADER_NAME = 'ProdBeginningBates';
import {
  setFillerAudioFileList,
  setIsFieldsChanged,
  setMP3DataComponent,
  setRedactionTagList,
} from '../../../../redux/slice/productionSlice';
import ArrLoadingSpinner from '../../../common/arr-loading-spinner/ArrLoadingSpinner';

// Type for child component ref
export interface StepChildRef {
  save: () => void;
  next: () => void;
}

// Utility function to convert flat array to tree structure
export const transformTagTreeData = (
  flatArray: any[]
): TreeViewBaseItem<ExtendedTreeItemProps>[] => {
  const itemMap = new Map();
  const rootItems: TreeViewBaseItem<ExtendedTreeItemProps>[] = [];

  // First pass: create all items
  flatArray.forEach((item) => {
    itemMap.set(item.id, {
      id: item.id,
      label: item.text,
      children: [],
      isGroup: item.isGroup,
      type: item.type,
      documentCount: item.documentCount,
      isSelected: item.isSelected,
      // Add other properties as needed
      ...item,
    });
  });

  // Second pass: build hierarchy
  flatArray.forEach((item) => {
    const treeItem = itemMap.get(item.id);

    if (
      item.parent === '#' ||
      item.parent === null ||
      item.parent === undefined
    ) {
      // Root level items (parent is "#" or null/undefined)
      rootItems.push(treeItem);
    } else {
      // Child item - find parent and add to its children
      const parent = itemMap.get(item.parent);
      if (parent) {
        parent.children.push(treeItem);
      } else {
        // Parent not found, treat as root item
        rootItems.push(treeItem);
      }
    }
  });

  return rootItems;
};

interface ProductionComponentsProps {
  isProductionSet: boolean;
  productionId: number;
  exportProductionId: number;
  getStatus?: (showToast: boolean, status: ToastType, message?: string) => void;
  goNext?: () => void;
  goBack?: () => void;
  onStateChange?: (state: {saveDisabled: any; nextDisabled: boolean}) => void;
  stepsMovedNext?: Record<string, boolean>;
  mode?: FormMode;
  isTemplate?: boolean;
  templateId?: number;
  productionStatusCode?: string;
  isUpdateNeeded?: boolean;
  onUpdateNeededChange?: (value: boolean) => void;
}

type OptionalSectionState = {
  id: string;
  checked: boolean;
};

const ProductionComponents = forwardRef<
  StepChildRef,
  ProductionComponentsProps
>(
  (
    {
      productionId,
      isProductionSet,
      exportProductionId = 0,
      getStatus,
      goNext,
      goBack: _goBack,
      onStateChange,
      stepsMovedNext,
      mode = FormMode.CREATE,
      isTemplate = false,
      templateId,
      productionStatusCode = null,
      isUpdateNeeded = false,
      onUpdateNeededChange,
    },
    ref
  ) => {
    const [isSaved, _setIsSaved] = useState<boolean>(false);
    const [tiffGetConfig, setTiffGetConfig] = useState<
      (() => TiffComponentModel | undefined) | null
    >(null);
    const [tiffRefetch, setTiffRefetch] = useState<(() => void) | null>(null);
    const [isNativeDirty, setIsNativeDirty] = useState(false);
    const [isNativeBaselineReady, setIsNativeBaselineReady] = useState(
      () => productionId <= 0
    );
    const isNativeDirtyRef = useRef(false);
    const isUpdateNeededRef = useRef(isUpdateNeeded);

    useEffect(() => {
      isNativeDirtyRef.current = isNativeDirty;
    }, [isNativeDirty]);

    useEffect(() => {
      isUpdateNeededRef.current = isUpdateNeeded;
    }, [isUpdateNeeded]);

    // Section selection states - can be used anywhere in the component
    // For PRODUCTION, DAT should be selected by default (true)
    // For EXPORT, DAT starts unchecked (false) until API data loads
    const [isDATSelected, setIsDATSelected] = useState<boolean>(
      isProductionSet ? true : false
    );
    const [isNativeSelected, setIsNativeSelected] = useState<boolean>(false);
    const [isTIFFSelected, setIsTIFFSelected] = useState<boolean>(false);
    const [isTextSelected, setIsTextSelected] = useState<boolean>(false);
    const [isPDFSelected, setIsPDFSelected] = useState<boolean>(false);
    const [isArchiveSelected, setIsArchiveSelected] = useState<boolean>(false);

    const [isMP3FileSelection, setIsMP3FileSelection] =
      useState<boolean>(false);
    const [isTransalationsSelection, setIsTransalationsSelection] =
      useState<boolean>(false);

    const [optionalSectionsState, setOptionalSectionsState] = useState<
      OptionalSectionState[]
    >([]);

    const handleOptionalSectionsChange = (newState: OptionalSectionState[]) => {
      const isTranslation =
        newState.find((s) => s.id === 'Translations')?.checked || false;
      const isMP3FileSelected =
        newState.find((s) => s.id === 'MP3 Files Processed for Audio Review')
          ?.checked || false;

      setIsTransalationsSelection(isTranslation);
      setIsMP3FileSelection(isMP3FileSelected);
      setOptionalSectionsState(newState);
    };

    const [_tiffPdfState, setTiffPdfState] = useState({
      pageOption: 0,
      pageType: 0,
      pageSize: 0,
      rotation: '0',
      pageOrientation: 0,
      skipNativeProduction: false,
      blankPageRemoval: false,
      preserveColor: false,
      brandingLocation: 'leftHeaderBranding',
      fontFamily: DEFAULT_FONT_FAMILY,
      fontStyle: DEFAULT_FONT_STYLE,
      fontSize: DEFAULT_FONT_SIZE,
      colorJpgOption: PreserveColorOption.ALL_DOCS,
      documentSelectionMethod: DocumentSelectionMethod.FILE_TYPE,
      selectedDocuments: [] as ISelectItem[],
      selectedItemsForChips: [] as {id: string; value: string}[],
      defaultBrandingData: {} as Record<string, unknown>,
      customBrandingData: {} as Record<string, unknown>,
      expandedPlusButtons: {} as Record<string, boolean>,
    });

    const [_nativeState, setNativeState] = useState<{
      nativeSelectedFileGroups: string;
      nativeSelectedTags: string;
    }>({
      nativeSelectedFileGroups: '',
      nativeSelectedTags: '',
    });
    const {productionSetId, securityGroupId} = useSelector(
      (state: RootState) => state.production
    );

    // Determine moduleType from isProductionSet using enum
    const moduleType = useMemo(
      () => (isProductionSet ? ModuleType.PRODUCTION : ModuleType.EXPORT),
      [isProductionSet]
    );

    const {
      data: productionComponentsDataFromProduction,
      isLoading: isProductionComponentsLoadingFromProduction,
      error: productionComponentsErrorFromProduction,
    } = useGetProductionComponents(
      {
        SecurityGroupId: securityGroupId,
        moduletype: moduleType,
        parentsetid: productionSetId,
        productionId: productionId,
        exportProductionId: exportProductionId || 0,
      },
      !!securityGroupId && !!productionSetId && productionId > 0 && !isTemplate
    );

    const {
      data: productionComponentsDataFromTemplate,
      isLoading: isProductionComponentsLoadingFromTemplate,
      error: productionComponentsErrorFromTemplate,
    } = useGetTemplateComponents(
      {
        templateId: templateId!,
        securityGroupId: securityGroupId,
      } as GetTemplateDetailsRequestParam,
      !!securityGroupId && !!templateId && isTemplate
    );

    // Use the appropriate data based on isTemplate flag
    const productionComponentsData = isTemplate
      ? productionComponentsDataFromTemplate
      : productionComponentsDataFromProduction;
    const isProductionComponentsLoading = isTemplate
      ? isProductionComponentsLoadingFromTemplate
      : isProductionComponentsLoadingFromProduction;
    const productionComponentsError = isTemplate
      ? productionComponentsErrorFromTemplate
      : productionComponentsErrorFromProduction;

    // Fetch TranslationComponent data here so it's always loaded
    const {data: translationComponentDataFromProduction} =
      useGetTranslationComponent(
        {
          SecurityGroupId: securityGroupId,
          moduletype: moduleType,
          parentsetid: productionSetId,
          productionId: productionId,
          exportProductionId: exportProductionId || 0,
        },
        !!securityGroupId &&
          !!productionSetId &&
          !isTemplate &&
          productionId > 0
      );

    const {data: translationComponentDataFromTemplate} =
      useGetTemplateTranslationComponent(
        {
          templateId: templateId!,
          securityGroupId: securityGroupId,
        } as GetTemplateDetailsRequestParam,
        !!securityGroupId && !!templateId && isTemplate
      );

    // Use the appropriate data based on isTemplate flag
    const translationComponentData = isTemplate
      ? translationComponentDataFromTemplate
      : translationComponentDataFromProduction;

    const dispatch = useDispatch();

    const handleNativeBaselineReady = useCallback(() => {
      isNativeDirtyRef.current = false;
      setIsNativeDirty(false);
      setIsNativeBaselineReady(true);
      dispatch(setIsFieldsChanged({isFieldsChanged: false}));
    }, [dispatch]);

    useEffect(() => {
      if (productionId > 0 && isNativeSelected) {
        setIsNativeBaselineReady(false);
      }
    }, [productionId, isNativeSelected, productionComponentsData]);

    useEffect(() => {
      if (productionComponentsData?.fillerAudioFileList)
        dispatch(
          setFillerAudioFileList(productionComponentsData?.fillerAudioFileList)
        );
      if (
        productionComponentsData?.productionComponentModel
          ?.mP3ComponentModelData
      )
        dispatch(
          setMP3DataComponent(
            productionComponentsData?.productionComponentModel
              .mP3ComponentModelData
          )
        );
      if (productionComponentsData?.redactionTagList)
        dispatch(
          setRedactionTagList(productionComponentsData?.redactionTagList)
        );
    }, [productionComponentsData]);

    const {
      mutate: saveProductionComponents,
      isPending: _isSaveProductionComponentsPending,
      error: _saveProductionComponentsError,
    } = useSaveProductionComponents();

    const tagTreeData = useMemo(() => {
      if (productionComponentsData?.tagTree) {
        const transformed = transformTagTreeData(
          productionComponentsData.tagTree
        );
        return transformed;
      }
      return [];
    }, [
      productionComponentsData,
      isProductionComponentsLoading,
      securityGroupId,
      productionSetId,
      productionId,
    ]);

    const schema = useMemo(() => {
      const messages = {
        datRequired: t(
          'productionsAndExports.createNewProduction.datComponent.errors.datRequired'
        ),
        atLeastOneFieldRequired: t(
          'productionsAndExports.createNewProduction.datComponent.errors.atLeastOneFieldRequired'
        ),
        prodBeginningBatesRequired: t(
          'productionsAndExports.createNewProduction.datComponent.errors.prodBeginningBatesRequired'
        ),
        prodBeginningBatesOrDocIdRequired: t(
          'productionsAndExports.createNewProduction.datComponent.errors.prodBeginningBatesOrDocIdRequired'
        ),
        tagsRequired: t(
          'productionsAndExports.createNewProduction.datComponent.errors.tagsRequired'
        ),
        fieldRequired: t(
          'productionsAndExports.createNewProduction.datComponent.errors.fieldRequired'
        ),
        customBrandingTagsAndTextRequired: t(
          'productionsAndExports.createNewProduction.productionComponents.tiffPdfComponent.errors.customBrandingTagsAndTextRequired'
        ),
        customBrandingTagsRequired: t(
          'productionsAndExports.createNewProduction.productionComponents.tiffPdfComponent.errors.customBrandingTagsRequired'
        ),
        customBrandingContentRequired: t(
          'productionsAndExports.createNewProduction.productionComponents.tiffPdfComponent.errors.customBrandingContentRequired'
        ),
        datFieldsRequired: t(
          'productionsAndExports.createNewProduction.datComponent.errors.datFieldsRequired'
        ),
        datFieldNameRequired: t(
          'productionsAndExports.createNewProduction.datComponent.errors.datFieldNameRequired'
        ),
        datFieldNameAlphabetStart: t(
          'productionsAndExports.createNewProduction.datComponent.errors.datFieldNameAlphabetStart'
        ),
        datFieldNameSpecialChars: t(
          'productionsAndExports.createNewProduction.datComponent.errors.datFieldNameSpecialChars'
        ),
        datFieldNameDuplicate: t(
          'productionsAndExports.createNewProduction.datComponent.errors.datFieldNameDuplicate'
        ),
        placeholderPrivRequired: t(
          'productionsAndExports.createNewProduction.productionComponents.tiffPdfComponent.errors.placeholderPrivRequired'
        ),
        placeholderTechIssueRequired: t(
          'productionsAndExports.createNewProduction.productionComponents.tiffPdfComponent.errors.placeholderTechIssueRequired'
        ),
        placeholderNativeDocsRequired: t(
          'productionsAndExports.createNewProduction.productionComponents.tiffPdfComponent.errors.placeholderNativeDocsRequired'
        ),
        redactionTagsRequired: t(
          'productionsAndExports.createNewProduction.productionComponents.tiffPdfComponent.errors.redactionTagsRequired'
        ),
        redactionTextRequired: t(
          'productionsAndExports.createNewProduction.productionComponents.tiffPdfComponent.errors.redactionTextRequired'
        ),
        generateMP3LoadFileRequired: t(
          'productionsAndExports.createNewProduction.productionComponents.mp3.errors.generateMP3LoadFileRequired'
        ),
        mP3RedactionTagsEmpty: t(
          'productionsAndExports.createNewProduction.productionComponents.mp3.errors.mP3RedactionTagsEmpty'
        ),
        nativeFileGroupOrTagRequired: t(
          'productionsAndExports.createNewProduction.productionComponents.native.errors.nativeFileGroupOrTagRequired'
        ),
        nativeRedactionTagsRequired: t(
          'productionsAndExports.createNewProduction.productionComponents.native.errors.nativeRedactionTagsRequired'
        ),
      };

      return productionComponentsSchema(messages, moduleType);
    }, [moduleType, t]);

    const defaultValues = schema.parse({
      // For PRODUCTION, DAT component should be selected by default.
      // For EXPORT, DAT is optional and will start unchecked unless API says otherwise.
      datSelected: moduleType === ModuleType.PRODUCTION,
      datFields: [
        {
          id: 1,
          category: DAT_FIELD_CATEGORY_FIELD,
          sourceField: DEFAULT_DAT_SOURCE_FIELD_ID,
          datFieldHeaderName: DEFAULT_DAT_FIELD_HEADER_NAME,
          scrubRedactions: false,
          scrubPriv: false,
          selectedTags: [],
        },
      ],
      nativeSelected: false, // Set to false initially to avoid validation error on mount
      nativeSelectedFileGroups: '',
      nativeSelectedTags: '',
    });

    const methods = useForm({
      mode: 'onChange',
      resolver: zodResolver(schema),
      defaultValues,
    });

    const {
      setValue,
      handleSubmit,
      formState,
      trigger,
      watch,
      getValues,
      reset,
    } = methods;
    const {textComponentData, translationComponentModelData} = getValues();
    const {isSubmitting, isValid, isDirty} = formState;
    // When translation data is fetched, set it in the form
    useEffect(() => {
      if (translationComponentData) {
        const currentValues = getValues();
        reset({
          ...currentValues,
          translationComponentModelData: {
            produceLoadFile: translationComponentData.produceLoadFile || false,
            translationSelectedFileGroups:
              translationComponentData.translationSelectedFileGroups || '',
          },
        });
      }
    }, [translationComponentData, reset, getValues]);

    const mP3ComponentModelData = watch('mP3ComponentModelData');

    const mP3IsBurnRedactionSelected = watch(
      'mP3ComponentModelData.isBurnRedactionSelected'
    );
    const mP3IsRedactionByAnnotation = watch(
      'mP3ComponentModelData.isRedactionByAnnotation'
    );
    const mP3SpecifiedRedactionTagList = watch(
      'mP3ComponentModelData.specifiedRedactionTagList'
    );

    useEffect(() => {
      if (mP3ComponentModelData) {
        trigger('mP3ComponentModelData');
      }
    }, [
      mP3IsBurnRedactionSelected,
      mP3IsRedactionByAnnotation,
      mP3SpecifiedRedactionTagList,
      trigger,
    ]);

    const handleTiffPdfChange = (values: TiffPdfValues) => {
      setTiffPdfState(
        (prev) =>
          ({
            ...prev,
            ...values,
          }) as typeof prev
      );

      setValue('tiffPdfData', values, {shouldValidate: true});
      trigger(['tiffPdfData']);
    };

    const handleTiffConfigAvailable = (
      getConfigFn: () => TiffComponentModel
    ) => {
      setTiffGetConfig(() => getConfigFn);
    };

    const handleTiffRefetchAvailable = (refetchFn: () => void) => {
      setTiffRefetch(() => refetchFn);
    };
    // Handle native file groups and tags changes (like DAT's onFieldsChange)
    const handleNativeFileGroupsAndTagsChange = (
      fileGroups: string,
      tags: string
    ) => {
      // Update preserved state
      setNativeState({
        nativeSelectedFileGroups: fileGroups,
        nativeSelectedTags: tags,
      });
      // Update form values
      setValue('nativeSelectedFileGroups', fileGroups, {shouldValidate: true});
      setValue('nativeSelectedTags', tags, {shouldValidate: true});
      // Trigger validation on all fields that superRefine depends on
      trigger([
        'nativeSelected',
        'nativeSelectedFileGroups',
        'nativeSelectedTags',
        'nativeRedactionSettings',
      ]);
    };

    const handleNativeRedactionSettingsChange = useCallback(
      (redactionSettings: NativeRedactionSettingsState) => {
        setValue('nativeRedactionSettings', redactionSettings, {
          shouldValidate: true,
        });
        trigger(['nativeSelected', 'nativeRedactionSettings']);
      },
      [setValue, trigger]
    );

    const isNextDisabled =
      isSubmitting ||
      !isValid ||
      isProductionComponentsLoading ||
      (isNativeSelected && productionId > 0 && !isNativeBaselineReady);

    const isInProgressOrCompleted =
      productionStatusCode === ProductionStatus.COMPLETED ||
      productionStatusCode == ProductionStatus.INPROGRESS;

    const onValid = (data: ProductionComponentsFormData) => {
      // Defensive guard: for PRODUCTION module type, DAT component must be selected.
      // If the DAT checkbox is not selected, block the save/next and surface the
      // same error message used in schema validation.
      if (moduleType === ModuleType.PRODUCTION && !isDATSelected) {
        getStatus?.(
          true,
          ToastType.Error,
          t(
            'productionsAndExports.createNewProduction.datComponent.errors.datRequired'
          )
        );
        return;
      }

      const realDatConfig = (window as any).getDatConfig
        ? (window as any).getDatConfig()
        : null;

      const datComponentData = {
        ansiFormatType: realDatConfig?.ansiFormatType,
        fieldSeparator: realDatConfig?.fieldSeparator,
        textQualifier: realDatConfig?.textQualifier,
        newLineSeparator: realDatConfig?.newLineSeparator,
        multiValue: realDatConfig?.multiValue,
        dateFormatID: realDatConfig?.dateFormatID,
        dateFormat: realDatConfig?.dateFormat,
        overwriteMasterDate: realDatConfig?.overwriteMasterDate,
        isAnsi: realDatConfig?.isAnsi,
        eTextFormat: realDatConfig?.eTextFormat,
        prependCarriageReturn: realDatConfig?.prependCarriageReturn,

        datFieldMappingDataList:
          (data as any).datFields?.map((field: any, index: number) => ({
            selectedTypeId:
              field.category === DAT_FIELD_CATEGORY_TAGS
                ? DAT_FIELD_TYPE_TAGS
                : DAT_FIELD_TYPE_FIELDS,
            selectedSourceId:
              field.category === DAT_FIELD_CATEGORY_TAGS
                ? field.selectedTags?.map((tag: any) => tag.id).join(',') || ''
                : field.sourceField,
            datField: field.datFieldHeaderName,
            offSet: '',
            isPrivileged: field.scrubPriv || false,
            isRedacted: field.scrubRedactions || false,
            sequenceNo: index + 1,
          })) || [],
      };

      const realNativeConfig = (window as any).getNativeConfig
        ? (window as any).getNativeConfig()
        : null;

      // Use form data for file groups and tags (they're now part of the form schema)
      // Fall back to window.getNativeConfig() for other values
      const nativeComponentData = {
        isSelectAllFileTypes: realNativeConfig?.isSelectAllFileTypes,
        produceLoadFile: realNativeConfig?.produceLoadFile,
        generateHTMLforEmails: realNativeConfig?.generateHTMLforEmails,
        produceNativeForTechDocs: realNativeConfig?.produceNativeForTechDocs,
        producedLockedFiles: realNativeConfig?.producedLockedFiles,
        protectionOnExcelFile: realNativeConfig?.protectionOnExcelFile,
        nativeComponentmodel: realNativeConfig?.nativeComponentmodel,
        nativeSelectedFileGroups:
          data.nativeSelectedFileGroups ||
          realNativeConfig?.nativeSelectedFileGroups ||
          '',
        nativeSelectedTags:
          data.nativeSelectedTags || realNativeConfig?.nativeSelectedTags || '',
        eRedactedAndPrevilededFamily:
          realNativeConfig?.eRedactedAndPrevilededFamily,
        redactionSettings: realNativeConfig?.redactionSettings,
      };

      const tiffPdfComponentData = tiffGetConfig?.();

      const isTranslation =
        optionalSectionsState.find((s) => s.id === 'Translations')?.checked ||
        false;

      const isMP3FileSelected =
        optionalSectionsState.find(
          (s) => s.id === 'MP3 Files Processed for Audio Review'
        )?.checked || false;

      const savePayload = {
        moduleType: moduleType,
        isMarkComplete: false,
        // Use the actual DAT selection state captured by the form.
        // Validation (schema + onInvalid) will prevent API calls when
        // DAT is not selected for a production.
        isDATSelected,
        isNativeSelected,
        isTIFFSelected,
        isTextSelected,
        isPDFSelected,
        isArchiveSelected,
        isTranslation,
        isMP3FileSelected,
        productionId: productionId,
        productionSecurityGroupId: securityGroupId,
        datComponentModelData: datComponentData,
        nativeComponentModelData: nativeComponentData,
        textComponentData,
        tiffComponentModelData: tiffPdfComponentData ?? undefined,
        pdfComponentModelData: {},
        archiveFileComponentData: {},
        translationComponentModelData,
        mP3ComponentModelData: {
          produceLoadFile: mP3ComponentModelData?.produceLoadFile,
          redactionType: Number(mP3ComponentModelData?.redactionType ?? 0),
          isBurnRedactionSelected:
            mP3ComponentModelData?.isBurnRedactionSelected,
          isRedactionByAnnotation: Boolean(
            Number(mP3ComponentModelData?.redactionType ?? 0)
          ),
          specifiedRedactionTagList:
            mP3ComponentModelData?.specifiedRedactionTagList,
          fillerAudioId:
            mP3ComponentModelData?.redactionStyle?.label?.toString(),
        },
      };

      if ((data as Record<string, unknown>).action === FormAction.SAVE) {
        saveProductionComponents(savePayload, {
          onSuccess: (response: any) => {
            _setIsSaved(true);
            setIsNativeDirty(false);
            // Update parent state to track that update is needed after save
            onUpdateNeededChange?.(false);
            getStatus?.(
              true,
              ToastType.Success,
              (response as {message?: string; data?: {message?: string}})
                ?.message ||
                (response as {data?: {message?: string}})?.data?.message
            );
            // Refetch TIFF component data after successful save
            tiffRefetch?.();
          },
          onError: (error: any) => {
            getStatus?.(
              true,
              ToastType.Error,
              (
                error as {
                  response?: {data?: {message?: string}};
                  message?: string;
                }
              )?.response?.data?.message ||
                (error as {message?: string})?.message
            );
          },
        });
      }

      if ((data as Record<string, unknown>).action === FormAction.NEXT) {
        const shouldSaveOnNext =
          isNativeDirtyRef.current || isUpdateNeededRef.current;

        if (!shouldSaveOnNext) {
          goNext?.();
          return;
        }
        saveProductionComponents(
          {...savePayload, isMarkComplete: true},
          {
            onSuccess: () => {
              getStatus?.(
                true,
                ToastType.Success,
                t(
                  'productionsAndExports.createNewProduction.common.nextSuccessToast'
                )
              );
              // Refetch TIFF component data after successful save
              tiffRefetch?.();
              setIsNativeDirty(false);
              onUpdateNeededChange?.(false);
              goNext?.();
            },
            onError: (error: any) => {
              getStatus?.(
                true,
                ToastType.Error,
                (
                  error as {
                    response?: {data?: {message?: string}};
                    message?: string;
                  }
                )?.response?.data?.message ||
                  (error as {message?: string})?.message
              );
            },
          }
        );
      }
      dispatch(setIsFieldsChanged({isFieldsChanged: false}));
    };

    // Use the errors handleSubmit passes in: formState.errors captured at render
    // time is stale on the first submit and misses the fresh resolver result.
    const onInvalid = (errors: FieldErrors<ProductionComponentsFormData>) => {
      const COMMON_VALIDATION_ERROR = t(
        'productionsAndExports.createNewProduction.datComponent.errors.validationFailed'
      );
      let errorMessage = '';
      let messageType: ToastType = ToastType.Error;

      // Skip DAT errors when moduleType is EXPORT and DAT is not selected
      if (
        moduleType !== ModuleType.EXPORT ||
        (moduleType === ModuleType.EXPORT && isDATSelected)
      ) {
        if (errors.datSelected?.message) {
          errorMessage = errors.datSelected.message;
        } else if (errors.datFields) {
          if (Array.isArray(errors.datFields)) {
            // Check field-level errors first (they take precedence)
            const fieldErrors = errors.datFields.filter(
              (field) => field?.datFieldHeaderName?.message
            );
            if (fieldErrors.length > 0) {
              errorMessage = fieldErrors[0].datFieldHeaderName.message;
            }
          } else if (errors.datFields?.message) {
            // Only show object-level error if no field-level errors
            errorMessage = errors.datFields.message;
          }
        }
      }

      // Only check other errors if no DAT error was found (or if EXPORT with DAT not selected, skip DAT errors)
      if (!errorMessage && errors.tiffPdfData) {
        if (errors.tiffPdfData?.customBrandingData?.message) {
          errorMessage = String(
            errors.tiffPdfData?.customBrandingData?.message
          );
          messageType = ToastType.Warning;
        } else if (errors.tiffPdfData?.placeholderSettings) {
          if (errors.tiffPdfData?.placeholderSettings?.message) {
            errorMessage = String(
              errors.tiffPdfData.placeholderSettings.message
            );
          } else if (
            errors.tiffPdfData?.placeholderSettings?.enabledforNativeDocs
              ?.message
          ) {
            errorMessage = String(
              errors.tiffPdfData.placeholderSettings.enabledforNativeDocs
                .message
            );
            messageType = ToastType.Warning;
          }
        } else if (errors.tiffPdfData?.redactionSettings?.message) {
          errorMessage = String(errors.tiffPdfData.redactionSettings.message);
          messageType = ToastType.Warning;
        } else if (errors.tiffPdfData?.redactionSettings) {
          if (
            errors.tiffPdfData?.redactionSettings?.redactionDataList?.message
          ) {
            errorMessage = String(
              errors.tiffPdfData.redactionSettings.redactionDataList?.message
            );
          }
        } else {
          errorMessage = COMMON_VALIDATION_ERROR;
        }
      } else if (errors.nativeSelected?.message) {
        errorMessage = String(errors.nativeSelected.message);
      } else if (errors.nativeRedactionSettings?.message) {
        errorMessage = String(errors.nativeRedactionSettings.message);
        messageType = ToastType.Warning;
      }

      if (!errorMessage) {
        if (errors.mP3ComponentModelData?.produceLoadFile?.message) {
          errorMessage = String(
            errors.mP3ComponentModelData?.produceLoadFile?.message
          );
        } else if (
          errors.mP3ComponentModelData?.specifiedRedactionTagList?.message
        ) {
          errorMessage = String(
            errors.mP3ComponentModelData?.specifiedRedactionTagList?.message
          );
        }
      }

      getStatus?.(
        true,
        messageType,
        String(errorMessage || COMMON_VALIDATION_ERROR)
      );
    };

    useImperativeHandle(ref, () => ({
      save: () => {
        setValue('action', FormAction.SAVE);
        handleSubmit(onValid, onInvalid)();
      },
      next: () => {
        if (isInProgressOrCompleted) {
          goNext?.();
          return;
        }
        setValue('action', FormAction.NEXT);
        handleSubmit(onValid, onInvalid)();
      },
    }));

    const handleCheckChange = (sectionId: string, checked: boolean) => {
      setSections((prev) =>
        prev.map((sec) => (sec.id === sectionId ? {...sec, checked} : sec))
      );

      // Update corresponding state variables
      switch (sectionId) {
        case 'DAT':
          setIsDATSelected(checked);
          // Set datSelected form field
          setValue('datSelected', checked, {
            shouldValidate: true,
            shouldDirty: true,
          });
          if (checked) {
            // When checking DAT, trigger validation to check fields
            trigger(['datSelected', 'datFields']);
          }
          break;
        case 'Native':
          setIsNativeSelected(checked);
          // Set nativeSelected form field first (like TIFF/PDF sets tiffPdfData)
          setValue('nativeSelected', checked, {
            shouldValidate: true,
            shouldDirty: true,
          });
          if (checked) {
            // When checking Native, trigger validation to check file groups/tags
            // Trigger all fields that superRefine depends on
            trigger([
              'nativeSelected',
              'nativeSelectedFileGroups',
              'nativeSelectedTags',
              'nativeRedactionSettings',
            ]);
          }
          break;
        case 'TIFF / PDF':
          setIsTIFFSelected(checked);
          if (!checked) {
            // Clear tiffPdfData when unchecked to avoid any validation
            setValue('tiffPdfData', undefined, {
              shouldValidate: false,
              shouldDirty: true,
            });
          } else {
            // When re-checking, always restore tiffPdfData from preserved state
            setValue('tiffPdfData', _tiffPdfState as TiffPdfValues, {
              shouldValidate: true,
              shouldDirty: true,
            });
            // Trigger validation after restoring data to catch any issues
            trigger('tiffPdfData');
          }
          break;
        case 'Text':
          setIsTextSelected(checked);
          break;
        case 'PDF':
          setIsPDFSelected(checked);
          break;
        case 'Archive':
          setIsArchiveSelected(checked);
          break;
      }

      const fieldMap: Record<string, keyof ProductionComponentsFormData> = {
        DAT: 'datSelected',
        Native: 'nativeSelected',
        'TIFF / PDF': 'tiffSelected',
        Text: 'textSelected',
        Archive: 'archiveSelected',
        Translation: 'translationSelected',
        MP3: 'mp3Selected',
      };

      const fieldName = fieldMap[sectionId];
      if (fieldName) {
        setValue(fieldName, checked, {
          shouldValidate: true,
          shouldDirty: true,
        });
        // When toggling TIFF/PDF, re-run conditional validation
        if (sectionId === 'TIFF / PDF') {
          trigger(['tiffSelected', 'tiffPdfData']);
        }
        // When toggling Native, re-run conditional validation (like TIFF/PDF)
        if (sectionId === 'Native') {
          // Trigger all fields that superRefine depends on to ensure validation runs
          // superRefine checks: if (data.nativeSelected) then validates fileGroups and tags
          trigger([
            'nativeSelected',
            'nativeSelectedFileGroups',
            'nativeSelectedTags',
          ]);
        }
        // When toggling DAT, re-run conditional validation
        if (sectionId === 'DAT') {
          // Trigger all fields that superRefine depends on to ensure validation runs
          // superRefine checks: if (data.datSelected) then validates datFields
          trigger(['datSelected', 'datFields']);
        }
      }
    };

    const isSaveDisabled =
      isSubmitting ||
      (!isDirty && (isSaved || stepsMovedNext?.productionComponents));

    useEffect(() => {
      _setIsSaved(false);
    }, [
      isDATSelected,
      isNativeSelected,
      isTIFFSelected,
      isTextSelected,
      isTransalationsSelection,
      isMP3FileSelection,
    ]);

    // Keep the form's mp3Selected field in sync so the schema can gate
    // MP3 validation the same way it gates TIFF/DAT/Native sections.
    useEffect(() => {
      setValue('mp3Selected', isMP3FileSelection, {shouldValidate: true});
    }, [isMP3FileSelection, setValue]);

    useEffect(() => {
      dispatch(setIsFieldsChanged({isFieldsChanged: false}));
    }, []);

    // Trigger validation when DAT checkbox is toggled
    useEffect(() => {
      if (isDATSelected) {
        // When DAT is checked, trigger validation to ensure fields are valid
        trigger(['datSelected', 'datFields']);
      }
    }, [isDATSelected, trigger]);

    useEffect(() => {
      onStateChange?.({
        saveDisabled: isSaveDisabled || isSaved,
        nextDisabled: isNextDisabled,
      });
    }, [isSaveDisabled, isNextDisabled, onStateChange, isSaved]);

    const [sections, setSections] = useState([
      {
        id: 'DAT',
        title: {
          primaryText: t(
            'productionsAndExports.createNewProduction.productionComponents.sectionTitles.dat'
          ),
          secondaryText:
            moduleType === ModuleType.EXPORT ||
            mode === FormMode.VIEW ||
            isInProgressOrCompleted
              ? ''
              : t(
                  'productionsAndExports.createNewProduction.productionComponents.sectionTitles.required'
                ),
        },
        content: (
          <DatComponent
            productionId={productionId}
            isProductionSet={isProductionSet}
            exportProductionId={exportProductionId}
            tagTreeData={tagTreeData}
            onFieldsChange={(fields) => {
              setValue('datFields', fields);
              trigger('datFields');
            }}
            mode={isInProgressOrCompleted ? FormMode.VIEW : mode}
            isTemplate={isTemplate}
            templateId={templateId}
          />
        ),
        // Default DAT selection:
        // - For PRODUCTION: Always checked (true) regardless of API value
        // - For EXPORT: Use API value if available, otherwise false
        checked:
          moduleType === ModuleType.PRODUCTION
            ? true
            : (productionComponentsData?.productionComponentModel
                ?.isDATSelected ?? false),
      },
      {
        id: 'Native',
        title: {
          primaryText: t(
            'productionsAndExports.createNewProduction.productionComponents.sectionTitles.native'
          ),
        },
        content: (
          <NativeComponent
            fileGroupList={productionComponentsData?.fileGroupTypeList || []}
            tagTreeData={tagTreeData}
            redactionTagList={productionComponentsData?.redactionTagList || []}
            nativeRedactionStyleList={
              productionComponentsData?.nativeRedactionStyleList || []
            }
            commonMetaDataList={
              productionComponentsData?.commonMetaDataList || []
            }
            commonProductionFieldList={
              productionComponentsData?.commonProductionFieldList || []
            }
            productionId={productionId}
            isProductionSet={isProductionSet}
            onFileGroupsAndTagsChange={handleNativeFileGroupsAndTagsChange}
            onRedactionSettingsChange={handleNativeRedactionSettingsChange}
            onNativeDirtyChange={setIsNativeDirty}
            onNativeBaselineReady={handleNativeBaselineReady}
            mode={isInProgressOrCompleted ? FormMode.VIEW : mode}
            isTemplate={isTemplate}
            templateId={templateId}
          />
        ),
        checked:
          productionComponentsData?.productionComponentModel?.isNativeSelected, // Will be updated from API
      },
      {
        id: 'TIFF / PDF',
        title: {
          primaryText: t(
            'productionsAndExports.createNewProduction.productionComponents.sectionTitles.tiffPdf'
          ),
        },
        content: (
          <TiffPdfComponent
            productionId={productionId}
            isProductionSet={isProductionSet}
            productionComponentsData={productionComponentsData}
            onChange={handleTiffPdfChange}
            onConfigAvailable={handleTiffConfigAvailable}
            onRefetchAvailable={handleTiffRefetchAvailable}
            initialValues={{}}
            mode={isInProgressOrCompleted ? FormMode.VIEW : mode}
            isTemplate={isTemplate}
            templateId={templateId}
          />
        ),
        checked:
          productionComponentsData?.productionComponentModel?.isTIFFSelected,
      },
      {
        id: 'Text',
        title: {
          primaryText: t(
            'productionsAndExports.createNewProduction.productionComponents.sectionTitles.text'
          ),
        },
        content: (
          <TextComponent
            ansiList={productionComponentsData?.ansiTypeList || []}
            productionId={productionId}
            isProductionSet={isProductionSet}
            mode={isInProgressOrCompleted ? FormMode.VIEW : mode}
            isTemplate={isTemplate}
            templateId={templateId}
          />
        ),
        checked:
          productionComponentsData?.productionComponentModel?.isTextSelected,
      },
    ]);

    useEffect(() => {
      setSections((prev) =>
        prev.map((section) =>
          section.id === 'DAT'
            ? {
                ...section,
                content: (
                  <DatComponent
                    productionId={productionId}
                    isProductionSet={isProductionSet}
                    exportProductionId={exportProductionId}
                    tagTreeData={tagTreeData}
                    onFieldsChange={(fields) => {
                      setValue('datFields', fields);
                      trigger('datFields');
                    }}
                    mode={isInProgressOrCompleted ? FormMode.VIEW : mode}
                    isTemplate={isTemplate}
                    templateId={templateId}
                  />
                ),
              }
            : section
        )
      );
    }, [tagTreeData, productionId, setValue]);

    useEffect(() => {
      setSections((prev) =>
        prev.map((section) =>
          section.id === 'Native'
            ? {
                ...section,
                content: (
                  <NativeComponent
                    fileGroupList={
                      productionComponentsData?.fileGroupTypeList || []
                    }
                    tagTreeData={tagTreeData}
                    redactionTagList={
                      productionComponentsData?.redactionTagList || []
                    }
                    nativeRedactionStyleList={
                      productionComponentsData?.nativeRedactionStyleList || []
                    }
                    commonMetaDataList={
                      productionComponentsData?.commonMetaDataList || []
                    }
                    commonProductionFieldList={
                      productionComponentsData?.commonProductionFieldList || []
                    }
                    productionId={productionId}
                    isProductionSet={isProductionSet}
                    onFileGroupsAndTagsChange={
                      handleNativeFileGroupsAndTagsChange
                    }
                    onRedactionSettingsChange={
                      handleNativeRedactionSettingsChange
                    }
                    onNativeDirtyChange={setIsNativeDirty}
                    onNativeBaselineReady={handleNativeBaselineReady}
                    mode={isInProgressOrCompleted ? FormMode.VIEW : mode}
                    isTemplate={isTemplate}
                    templateId={templateId}
                  />
                ),
              }
            : section
        )
      );
    }, [
      productionComponentsData?.fileGroupTypeList,
      tagTreeData,
      productionId,
    ]);

    useEffect(() => {
      setSections((prev) =>
        prev.map((section) =>
          section.id === 'Text'
            ? {
                ...section,
                content: (
                  <TextComponent
                    ansiList={productionComponentsData?.ansiTypeList || []}
                    productionId={productionId}
                    isProductionSet={isProductionSet}
                    mode={isInProgressOrCompleted ? FormMode.VIEW : mode}
                    isTemplate={isTemplate}
                    templateId={templateId}
                  />
                ),
              }
            : section
        )
      );
    }, [productionComponentsData?.ansiTypeList, productionId]);

    useEffect(() => {
      setSections((prev) =>
        prev.map((section) =>
          section.id === 'TIFF / PDF'
            ? {
                ...section,
                content: (
                  <TiffPdfComponent
                    isProductionSet={isProductionSet}
                    productionId={productionId}
                    productionComponentsData={productionComponentsData}
                    onChange={handleTiffPdfChange}
                    onConfigAvailable={handleTiffConfigAvailable}
                    onRefetchAvailable={handleTiffRefetchAvailable}
                    initialValues={{}}
                    mode={isInProgressOrCompleted ? FormMode.VIEW : mode}
                    isTemplate={isTemplate}
                    templateId={templateId}
                  />
                ),
              }
            : section
        )
      );
    }, [productionComponentsData, productionId]);

    useEffect(() => {
      if (productionComponentsData?.productionComponentModel) {
        const componentModel =
          productionComponentsData.productionComponentModel;

        // Update state variables from API data
        const isTIFFSelectedFromAPI =
          componentModel.isTIFFSelected ||
          componentModel.isPDFSelected ||
          false;

        // Set DAT selection from API
        // For PRODUCTION: Always set to true (checked) regardless of API value
        // For EXPORT: Use the API value
        const isDATSelectedFromAPI = componentModel.isDATSelected || false;
        const finalDATSelected =
          moduleType === ModuleType.PRODUCTION ? true : isDATSelectedFromAPI;
        setIsDATSelected(finalDATSelected);
        // Set form value for datSelected
        setValue('datSelected', finalDATSelected, {
          shouldValidate: true,
          shouldDirty: false,
        });
        const isNativeSelectedFromAPI =
          componentModel.isNativeSelected || false;
        setIsNativeSelected(isNativeSelectedFromAPI);
        setIsTIFFSelected(isTIFFSelectedFromAPI);
        setIsTextSelected(componentModel.isTextSelected || false);
        setIsPDFSelected(componentModel.isPDFSelected || false);
        setIsArchiveSelected(componentModel.isArchiveSelected || false);

        // Initialize Native state from API data (like TIFF/PDF pattern)
        if (
          isNativeSelectedFromAPI &&
          componentModel.nativeComponentModelData
        ) {
          const nativeData = componentModel.nativeComponentModelData;
          const nativeFileGroups = nativeData.nativeSelectedFileGroups || '';
          const nativeTags = nativeData.nativeSelectedTags || '';

          // Initialize preserved state
          setNativeState({
            nativeSelectedFileGroups: nativeFileGroups,
            nativeSelectedTags: nativeTags,
          });

          // Set form values
          setValue('nativeSelected', isNativeSelectedFromAPI, {
            shouldValidate: true,
            shouldDirty: false,
          });
          setValue('nativeSelectedFileGroups', nativeFileGroups, {
            shouldValidate: true,
            shouldDirty: false,
          });
          setValue('nativeSelectedTags', nativeTags, {
            shouldValidate: true,
            shouldDirty: false,
          });

          // Trigger validation after setting native values to catch any issues
          trigger([
            'nativeSelected',
            'nativeSelectedFileGroups',
            'nativeSelectedTags',
          ]);
        } else {
          // If Native is not selected, ensure form values are set to false/empty
          setValue('nativeSelected', false, {
            shouldValidate: true,
            shouldDirty: false,
          });
        }

        // Update form values for validation
        setValue('tiffSelected', isTIFFSelectedFromAPI, {
          shouldValidate: true,
          shouldDirty: false,
        });

        // Trigger validation after setting tiffSelected to catch any issues
        if (isTIFFSelectedFromAPI) {
          trigger(['tiffSelected', 'tiffPdfData']);
        }

        setSections((prev) =>
          prev.map((section) => {
            let checked = false;

            switch (section.id) {
              case 'DAT':
                // For PRODUCTION: Always checked (true) regardless of API value
                // For EXPORT: Use the API value
                checked =
                  moduleType === ModuleType.PRODUCTION
                    ? true
                    : componentModel.isDATSelected || false;
                break;
              case 'Native':
                checked = componentModel.isNativeSelected;
                break;
              case 'TIFF / PDF':
                checked =
                  componentModel.isTIFFSelected || componentModel.isPDFSelected;
                break;
              case 'Text':
                checked = componentModel.isTextSelected;
                break;
              case 'Archive':
                checked = componentModel.isArchiveSelected;
                break;
              case 'Translation':
                checked = componentModel.isTranslation;
                break;
              case 'MP3':
                checked = componentModel.isMP3FileSelected;
                break;
              default:
                checked = false;
            }

            return {
              ...section,
              checked,
            };
          })
        );
      }
    }, [productionComponentsData?.productionComponentModel, setValue, trigger]);
    if (
      isProductionComponentsLoading ||
      productionComponentsError ||
      !productionComponentsData
    ) {
      return (
        <Box className="new-production-form-section">
          <Box className="new-production-form production-components">
            <ArrLoadingSpinner />
          </Box>
        </Box>
      );
    }

    return (
      <FormProvider {...methods}>
        <Box
          className="new-production-form-section"
          component="form"
          onSubmit={handleSubmit(onValid, onInvalid)}
        >
          <Box className="new-production-form production-components">
            <ArrAccordionWithCheckbox
              readAccordionTextIcon={true}
              sections={sections}
              onCheckChange={handleCheckChange}
              disableCheckboxEdit={
                isInProgressOrCompleted || mode === FormMode.VIEW
              }
              allowMultiple={true}
            />
            <OptionalInstruction
              productionId={productionId || 0}
              isProductionSet={isProductionSet}
              productionComponentsData={productionComponentsData}
              translationComponentData={translationComponentData}
              onStateChange={handleOptionalSectionsChange}
              mode={isInProgressOrCompleted ? FormMode.VIEW : mode}
              isTemplate={isTemplate}
              templateId={templateId}
              isInProgressOrCompleted={isInProgressOrCompleted}
            />
          </Box>
        </Box>
      </FormProvider>
    );
  }
);

ProductionComponents.displayName = 'ProductionComponents';

export default ProductionComponents;
